import { useTranslation } from 'react-i18next';
import { useAppear } from '../hooks/useAppear';
import { useCountUp, useInView } from '../hooks/useCountUp';
import {
  IcoUsers, IcoHome, IcoMapPin, IcoTarget,
  IcoBook, IcoActivity, IcoShield, IcoTrendingUp
} from '../assets/icons';

const statsData = [
  { Icon: IcoUsers,      pt: 'Comunidades Apoiadas',      en: 'Communities Supported',    val: 50,    suf: '+' },
  { Icon: IcoBook,       pt: 'Crianças Beneficiadas',     en: 'Children Benefited',       val: 20000, suf: '+' },
  { Icon: IcoUsers,      pt: 'Mulheres Empoderadas',      en: 'Women Empowered',          val: 5000,  suf: '+' },
  { Icon: IcoActivity,   pt: 'Consultas Médicas',         en: 'Medical Consultations',    val: 30000, suf: '+' },
  { Icon: IcoHome,       pt: 'Famílias Apoiadas',         en: 'Families Supported',       val: 8000,  suf: '+' },
  { Icon: IcoShield,     pt: 'Jovens em Formação',        en: 'Youth in Training',        val: 3000,  suf: '+' },
  { Icon: IcoTarget,     pt: 'Programas Activos',         en: 'Active Programmes',        val: 8,     suf: '' },
  { Icon: IcoMapPin,     pt: 'Províncias Alcançadas',     en: 'Provinces Reached',        val: 10,    suf: '+' },
];

function StatCard({ Icon, pt, en, val, suf, isPt }) {
  const { count, ref } = useCountUp(val, 2400);
  const display = val >= 1000 ? (count / 1000).toFixed(0) + 'K' : count;

  return (
    <div
      ref={ref}
      className="card-base p-7 text-center hover:-translate-y-1 transition-transform group"
    >
      <div className="w-12 h-12 rounded-xl border border-brand-100 dark:border-brand-900 flex items-center justify-center text-brand-600 mx-auto mb-4 group-hover:bg-brand-600 group-hover:border-brand-600 group-hover:text-white transition-all">
        <Icon cls="w-6 h-6" />
      </div>
      <div className="text-4xl font-bold text-brand-600 mb-2">{display}{suf}</div>
      <div className="text-sm text-brand-600 dark:text-brand-500 font-medium leading-snug">
        {isPt ? pt : en}
      </div>
    </div>
  );
}

function ProgressBar({ label, pct }) {
  const { inView, ref } = useInView(0.3);
  return (
    <div ref={ref}>
      <div className="flex justify-between items-center text-sm mb-2">
        <span className="text-brand-700 dark:text-brand-400 font-medium">{label}</span>
        <span className="font-bold text-brand-900 dark:text-white tabular-nums">{pct}%</span>
      </div>
      <div className="h-2 bg-brand-100 dark:bg-brand-900 rounded-full overflow-hidden">
        <div
          className="h-full bg-brand-600 rounded-full transition-all ease-out"
          style={{
            width: inView ? `${pct}%` : '0%',
            transitionDuration: '1.6s',
          }}
        />
      </div>
    </div>
  );
}

/* Angola map using real image from public folder */
function AngolaMap({ isPt }) {
  return (
    <div>
      <img
        src="/mapa.png"
        alt={isPt ? 'Mapa de Angola' : 'Map of Angola'}
        className="w-full max-w-xs mx-auto block"
      />
      <p className="text-center text-[11px] text-brand-500 mt-3">
        {isPt
          ? 'Presença activa em 10+ províncias angolanas'
          : 'Active presence in 10+ Angolan provinces'}
      </p>
    </div>
  );
}

/* Programmes results table */
const progResults = [
  { prog: 'PDIC',             pt: 'Projecto piloto activo na comunidade do Toco, Huíla.',                   en: 'Pilot project active in the Toco community, Huíla.'           },
  { prog: 'Tata Uhayele',     pt: '30.000+ consultas realizadas em zonas rurais e de difícil acesso.',      en: '30,000+ consultations in rural and hard-to-reach areas.'       },
  { prog: 'Seja Mulher',      pt: '5.000+ mulheres capacitadas em empreendedorismo e liderança.',           en: '5,000+ women trained in entrepreneurship and leadership.'       },
  { prog: 'Pé Boto',          pt: 'Centenas de crianças com nova mobilidade após cirurgia ortopédica.',     en: 'Hundreds of children with new mobility after orthopaedic surgery.' },
  { prog: 'Campus Juventude', pt: '3.000+ jovens em programas de liderança e inovação.',                   en: '3,000+ youth in leadership and innovation programmes.'          },
  { prog: 'Dikota E6.0',      pt: 'Inclusão activa de idosos em múltiplas províncias angolanas.',          en: 'Active elderly inclusion across multiple Angolan provinces.'    },
  { prog: 'GSS 2030',         pt: 'Campanhas de sensibilização sobre VIH/SIDA em todo o território.',      en: 'HIV/AIDS awareness campaigns across the country.'               },
  { prog: 'Revieng Angola',   pt: 'Apoio humanitário directo a famílias em situação de vulnerabilidade.',  en: 'Direct humanitarian support for families in vulnerable situations.' },
];

export default function ImpactPage() {
  const { i18n } = useTranslation();
  const isPt = i18n.language === 'pt';
  const ref1 = useAppear();
  const ref2 = useAppear();
  const ref3 = useAppear();

  const bars = [
    { pt: 'Saúde (Tata Uhayele + Pé Boto + GSS 2030)',        en: 'Health (Tata Uhayele + Clubfoot + GSS 2030)',          pct: 35 },
    { pt: 'Desenvolvimento Comunitário (PDIC + Revieng)',      en: 'Community Development (PDIC + Revieng)',              pct: 28 },
    { pt: 'Empoderamento (Seja Mulher + Campus Juventude)',    en: 'Empowerment (Seja Mulher + Youth Campus)',            pct: 22 },
    { pt: 'Inclusão Social (Dikota E6.0)',                     en: 'Social Inclusion (Dikota E6.0)',                      pct: 15 },
  ];

  return (
    <main>
      {/* Hero */}
      <section className="relative bg-brand-950 pt-[68px] overflow-hidden">
        <div className="absolute right-0 top-0 w-[600px] h-[600px] rounded-full bg-brand-600/8 blur-[120px] translate-x-1/2 -translate-y-1/3" />
        <div className="wrap relative z-10 py-24 text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-brand-800 bg-brand-900/60 mb-7">
            <span className="text-[11px] font-bold text-brand-500 uppercase tracking-[0.14em]">
              {isPt ? 'Dados Actualizados 2024' : 'Updated Data 2024'}
            </span>
          </div>
          <h1 className="display-xl text-white mb-6">
            {isPt ? 'O Nosso Impacto' : 'Our Impact'}
          </h1>
          <p className="body-lg text-brand-400 max-w-xl mx-auto">
            {isPt
              ? 'Cada número representa uma vida transformada, uma família apoiada e uma comunidade com mais esperança.'
              : 'Each number represents a life transformed, a family supported and a community with more hope.'}
          </p>
        </div>
      </section>

      {/* Stats grid */}
      <section id="dashboard" className="section-pad bg-white dark:bg-brand-950">
        <div className="wrap">
          <div ref={ref1} className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {statsData.map((s, i) => (
              <StatCard key={i} {...s} isPt={isPt} />
            ))}
          </div>
        </div>
      </section>

      {/* Distribution + Map */}
      <section className="section-pad bg-brand-50 dark:bg-brand-900/20">
        <div className="wrap grid lg:grid-cols-2 gap-16 items-center">
          <div ref={ref2}>
            <p className="label-tag">{isPt ? 'Distribuição' : 'Distribution'}</p>
            <h2 className="display-md text-brand-900 dark:text-white mb-6">
              {isPt ? 'Impacto por Área de Actuação' : 'Impact by Area of Action'}
            </h2>
            <p className="body-md text-brand-600 dark:text-brand-500 mb-8">
              {isPt
                ? 'Os 8 programas da Fundação Ngana Zenza cobrem as principais necessidades das comunidades vulneráveis de Angola.'
                : 'The 8 Ngana Zenza Foundation programmes cover the main needs of Angola\'s vulnerable communities.'}
            </p>
            <div className="space-y-6">
              {bars.map((b, i) => (
                <ProgressBar key={i} label={isPt ? b.pt : b.en} pct={b.pct} />
              ))}
            </div>
          </div>

          <div ref={ref3}>
            <p className="label-tag">{isPt ? 'Cobertura Geográfica' : 'Geographic Coverage'}</p>
            <h2 className="display-md text-brand-900 dark:text-white mb-8">
              {isPt ? 'De Norte a Sul de Angola' : 'From North to South Angola'}
            </h2>
            <AngolaMap isPt={isPt} />
          </div>
        </div>
      </section>

      {/* Results by programme */}
      <section className="section-pad bg-white dark:bg-brand-950">
        <div className="wrap">
          <div className="text-center mb-12">
            <p className="label-tag justify-center">{isPt ? 'Resultados' : 'Results'}</p>
            <h2 className="display-md text-brand-900 dark:text-white">
              {isPt ? 'Resultados por Programa' : 'Results by Programme'}
            </h2>
          </div>

          <div className="max-w-3xl mx-auto divide-y divide-brand-100 dark:divide-brand-900 border border-brand-100 dark:border-brand-900 rounded-2xl overflow-hidden">
            {progResults.map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-5 px-6 py-5 bg-white dark:bg-brand-950 hover:bg-brand-50 dark:hover:bg-brand-900/40 transition-colors"
              >
                <div className="w-8 h-8 rounded-lg bg-brand-50 dark:bg-brand-900 border border-brand-100 dark:border-brand-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <IcoTrendingUp cls="w-4 h-4 text-brand-600" />
                </div>
                <div className="flex-1">
                  <span className="text-[11px] font-bold text-brand-600 uppercase tracking-widest block mb-0.5">
                    {item.prog}
                  </span>
                  <p className="text-sm text-brand-700 dark:text-brand-400 leading-relaxed">
                    {isPt ? item.pt : item.en}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}